import { WarningTriangle } from '@hiberworld/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'components/common/Link';
import { Button } from 'components/common/buttons/Button';
import { Select } from 'components/inputs/Select/Select';
import { TextArea } from 'components/inputs/TextArea/TextArea';
import { Spacer } from 'components/layouts/Spacer';
import { ErrorMessage, Heading1, Label, Paragraph } from 'components/typography';
import { useReportContent } from 'hooks/useReportContent';
import { useModal } from 'lib/useModal';
import { useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import { InformationModal } from '../InformationModal';
import { Modal } from '../Modal';
import * as S from '../Modal.styles';
import { ReportModalProps, ReportModalState } from './ReportModal.types';

const schema = yup
  .object({
    reason: yup.string().required('You need to select a reason'),
    message: yup.string().min(15, 'Your message need to be between 15 and 1024 characters').required(),
  })
  .required();

export const ReportModal = ({ close, content, contentType, next, projectId = null }: ReportModalProps) => {
  const [reportContent] = useReportContent(content);
  const haveBeenReportedPreviously = content?.isReported || ('haveIReported' in content && content.haveIReported);
  const theme = useTheme();
  const modal = useModal(
    <InformationModal
      headline="Thanks for your report!"
      text={`The moderators will investigate the reported ${contentType.toLowerCase()} and take any necessary actions`}
      confirmButtonText="Ok"
    />
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportModalState>({
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    close?.();
  };

  const onSubmit = (values: ReportModalState) => {
    reportContent({
      variables: {
        id: content.id,
        type: contentType,
        reason: values.reason,
        reasonText: values.message,
        rootContentId: content.id,
        projectId,
      },
    });

    if (next) {
      next();
      return;
    }

    modal.open();
  };

  const typename = content.__typename === 'Project' ? 'Game' : content.__typename;

  return (
    <Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Heading1 className="text-h2 text-center">Report {typename}</Heading1>
        <Spacer height={40} />
        {haveBeenReportedPreviously && (
          <>
            <div className="flex items-center">
              <WarningTriangle color={theme.primaryColor.yellow} size="1.5rem" />
              <Spacer width={8} />
              <S.Text fontSize="12px">Reporting again will replace your previous report</S.Text>
            </div>
            <Spacer height={8} />
          </>
        )}
        <Label size="large" className="mb-2" htmlFor="reason">
          Reason
        </Label>
        <Select id="reason" {...register('reason')} placeholder="Select reason">
          <option value="Sexual">Sexual content</option>
          <option value="Violence">Violent or repulsive content</option>
          <option value="Abusive">Hateful or abusive content</option>
          <option value="Harmful">Harmful or dangerous acts</option>
          <option value="Spam">Spam or misleading</option>
        </Select>
        <ErrorMessage message={errors.reason?.message} />

        <Label size="large" className="mb-2 mt-4" htmlFor="reasonText">
          Additional info for moderators
        </Label>
        <Spacer height={4} />
        <TextArea id="reasonText" placeholder="Reason for Report" {...register('message')} maxLength={1024} />
        <ErrorMessage message={errors.message?.message} className="mt-2" />

        <Spacer height={16} />
        <Paragraph size="small">
          Offensive and inappropriate content will be removed or suspended. Please see our{' '}
          <Link target="_blank" rel="noreferrer" href="https://hiber3d.com/rules">
            Rules{' '}
          </Link>
          for more information.
        </Paragraph>
        <Spacer height={16} />
        <div className="flex justify-center gap-4">
          <Button variety="secondary" size="medium" type="button" text="Cancel" onClick={handleClose} />
          <Button variety="primary" size="medium" text="Submit" type="submit" />
        </div>
      </form>
    </Modal>
  );
};

export default ReportModal;
